import { z } from 'zod'

import { PersonDisplay } from '@/models/features/display-column.models'

export const ChecklistStatusEnum = z.enum([
  'pending-review',
  'past-due',
  'not-started',
  'completed',
])
export type ChecklistStatus = z.infer<typeof ChecklistStatusEnum>

export const ChecklistTypeEnum = z.enum(['task', 'inspection'])
export type ChecklistType = z.infer<typeof ChecklistTypeEnum>

const ChecklistInputTypeSimpleEnum = z.enum([
  'single_choice',
  'multiple_choice',
  'text_info',
  'file_upload',
  'long_answer',
  'short_answer',
])
export type ChecklistInputTypeSimple = z.infer<typeof ChecklistInputTypeSimpleEnum>

export const ChecklistInputTypeEnum = z.enum([
  ...ChecklistInputTypeSimpleEnum.options,
  'providers_info',
  'form',
  'service',
  'vehicle_form',
])
export type ChecklistInputType = z.infer<typeof ChecklistInputTypeEnum>

export interface Checklist {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  residentDisplay?: PersonDisplay
  clientId: number
  clientName: string
  firstName?: string | null
  lastName?: string | null
  avatar?: string | null
  email?: string | null
  phone?: string | null
  buildingNumber?: string | null
  street?: string | null
  dueDate: number | null
  id: number
  moveInDate: number
  // Note: not returned by every endpoint yet - rendered as an empty value when missing.
  moveOutDate?: number | null
  moveInSortOrder: number
  name: string
  parentChecklistId: number
  status: ChecklistStatus
  type: ChecklistType
  unit: string
}

// Note: Base schema with common fields for all input types
const ChecklistInputBaseSchema = z.object({
  checklistId: z.number(),
  createdAt: z.number(),
  formActionLabel: z.string(),
  formMaxEntries: z.number(),
  id: z.number(),
  isRequired: z.boolean(),
  options: z.array(z.string()),
  position: z.number(),
  taskLabel: z.string(),
  textInfo: z.string(),
})

export const ChecklistInputSimpleSchema = ChecklistInputBaseSchema.extend({
  answers: z.array(z.string()),
  inputType: ChecklistInputTypeSimpleEnum,
})
export type ChecklistInputSimple = z.infer<typeof ChecklistInputSimpleSchema>

export const ChecklistInputFormSchema = ChecklistInputBaseSchema.extend({
  formItems: z.array(
    z.object({
      answers: z.array(z.array(z.string())),
      checklistInputId: z.number(),
      createdAt: z.number(),
      formActionLabel: z.string(),
      id: z.number(),
      inputType: ChecklistInputTypeSimpleEnum,
      isRequired: z.boolean(),
      options: z.array(z.string()),
      position: z.number(),
      taskLabel: z.string(),
    }),
  ),
  inputType: z.literal(ChecklistInputTypeEnum.enum.form),
})
export type ChecklistInputForm = z.infer<typeof ChecklistInputFormSchema>

export const ChecklistInputVehicleFormSchema = ChecklistInputFormSchema.extend({
  inputType: z.literal(ChecklistInputTypeEnum.enum.vehicle_form),
})
export type ChecklistInputVehicleForm = z.infer<typeof ChecklistInputVehicleFormSchema>

const ChecklistInputProviderInfoSchema = z.object({
  description: z.string(),
  id: z.number(),
  logo: z.string().nullable(),
  name: z.string(),
  phoneNumber: z.string(),
  siteUrl: z.string(),
  type: z.string(),
})
export type ChecklistInputProviderInfo = z.infer<typeof ChecklistInputProviderInfoSchema>

export const ChecklistInputProvidersInfoSchema = ChecklistInputBaseSchema.extend({
  inputType: z.literal(ChecklistInputTypeEnum.enum.providers_info),
  providersInfo: z.array(ChecklistInputProviderInfoSchema),
})
export type ChecklistInputProvidersInfo = z.infer<typeof ChecklistInputProvidersInfoSchema>

export const ChecklistInputServiceSchema = ChecklistInputBaseSchema.extend({
  bookingTypeId: z.number(),
  formActionLabel: z.string(),
  inputType: z.literal(ChecklistInputTypeEnum.enum.service),
})
export type ChecklistInputService = z.infer<typeof ChecklistInputServiceSchema>

export type ChecklistInput =
  | ChecklistInputSimple
  | ChecklistInputForm
  | ChecklistInputProvidersInfo
  | ChecklistInputService
  | ChecklistInputVehicleForm

export interface ChecklistTask {
  id: number
  name: string
  type: ChecklistType
  status: ChecklistStatus
  dueDate: number
  completedAt: number | null
  instructions: string
  attachment: string | null
  approvalRequired: boolean
  signatureRequired: boolean
  signatures: {
    date: number
    signature: string
  }[]
  revisionRequestNote: string | null
  parentChecklistId: number
  resident: {
    email: string
    firstName: string
    lastName: string
    avatar: string
    clientId: number
    apartment: string
    phone: number
    street: string
    moveInDate: number
  }
  inputs: ChecklistInput[]
  building: {
    id: number
    name: string
    timezone: string
    code: number
  }
}
